import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import type {Message, SessionDetail, SessionSummary} from '../colimind-types';
import {ColimindChatStreamEvent} from "./colimind-api.service";
import {truncate} from "./colimind-settings.service";

/** Cutoff for a session title derived from the first question. */
const SESSION_TITLE_MAX_LEN = 80;

@Injectable({ providedIn: 'root' })
export class ColimindService {

  // prefix for colimind sessions in storage
  public static COLIMIND_STORAGE_PREFIX: string = 'COLIMIND_SESSION_';

  private storage: Storage;

  constructor() {
    this.storage = localStorage;
  }

  listSessions(userIdentifier: string): Observable<SessionSummary[]> {
    return of(this.getSessionsFromStorage(userIdentifier));
  }

  getSession(sessionId: string): Observable<SessionDetail | null> {
    return of(this.getFromStorage(sessionId)?.session_detail ?? null);
  }

  /** Storage key of the user's stored chat with this thread, or null when there is none. */
  findSessionByThread(userIdentifier: string, threadId: string): string | null {
    if (!userIdentifier || !threadId) return null;
    const sessionId = ColimindService.COLIMIND_STORAGE_PREFIX + userIdentifier + '_' + threadId;
    return this.getFromStorage(sessionId) ? sessionId : null;
  }

  getCurrentSessionThreadId(sessionId: string): string {
    return this.getFromStorage(sessionId)?.thread_id ?? '';
  }

  deleteSession(sessionId: string): Observable<void> {
    return of(this.deleteFromStorage(sessionId));
  }

  /**
   * Writes the current state of a thread to storage. Returns the storage key, or null when
   * nothing could be persisted (no user, no event, or the storage quota is exhausted) --
   * the chat itself keeps working in that case, it is just not restorable afterwards.
   */
  saveSession(event: ColimindChatStreamEvent, userIdentifier: string, currentMessages: Message[]): string | null {
    if (!userIdentifier || !event) {
      return null;
    }
    // session creation - currently we just override the whole saved session with the updated one received from backend
    const sessionId = ColimindService.COLIMIND_STORAGE_PREFIX + userIdentifier + '_' + event.threadId;

    // date timestamp conversion
    const completed = this.eventTimestampToIso(event.metaData?.['completedAt']);

    // Title and creation date belong to the first message of a thread and must not move with
    // every follow-up question, so they are carried over from the already stored session.
    const existing = this.getFromStorage(sessionId);
    const title = existing?.title ?? this.deriveTitle(event, currentMessages);
    const created = existing?.created_at ?? this.eventTimestampToIso(event.metaData?.['createdAt']);

    const threadId = event.threadId;
    const traceId: string = event.metaData?.['traceId'];
    const messageId: string = event.metaData?.['messageId'];

    // create session and add to storage
    const sessionSummary: SessionSummary = {
      session_id: sessionId,
      title,
      created_at: created,
      updated_at: completed,
      message_count: currentMessages.length,
      thread_id: threadId,
      session_detail: {
        session_id: sessionId,
        title,
        created_at: created,
        updated_at: completed,
        trace_id: traceId,
        message_id: messageId,
        messages: currentMessages
      }
    };
    return this.saveToStorage(sessionId, sessionSummary, userIdentifier) ? sessionId : null;
  }

  /** Prompt from the event metadata, falling back to the first question of the thread. */
  private deriveTitle(event: ColimindChatStreamEvent, currentMessages: Message[]): string {
    const prompt: string = event.metaData?.['prompt'];
    const raw = prompt || currentMessages.find(m => m.role === 'user')?.text || '';
    return truncate(raw, SESSION_TITLE_MAX_LEN);
  }

  // current storage handling used for local storage of session/thread/conversations
  /** Returns false when the entry could not be persisted (quota exhausted). */
  private saveToStorage(identifier: string, event: any, userIdentifier?: string): boolean {
    const payload = JSON.stringify(event);
    try {
      this.storage.setItem(identifier, payload);
      return true;
    } catch {
      // Storage is full -- drop the oldest other session of this user and retry once.
      const oldest = this.getSessionsFromStorage(userIdentifier)
        .filter(s => s.session_id !== identifier)
        .pop();
      if (!oldest) {
        return false;
      }
      this.deleteFromStorage(oldest.session_id);
      try {
        this.storage.setItem(identifier, payload);
        return true;
      } catch {
        return false;
      }
    }
  }

  private getFromStorage(identifier: string): SessionSummary | null {
    if (!identifier) {
      return null;
    }
    const raw = this.storage.getItem(identifier);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as SessionSummary;
    } catch {
      // A corrupt entry would break the whole history list, so drop it.
      this.deleteFromStorage(identifier);
      return null;
    }
  }

  private deleteFromStorage(identifier: string): void {
    return this.storage.removeItem(identifier);
  }

  /** All sessions of the given user, most recently updated first. */
  private getSessionsFromStorage(userIdentifier: string): SessionSummary[] {
    if (!userIdentifier) {
      return [];
    }
    const sessions: SessionSummary[] = [];
    // Trailing separator: keys end in '_<threadId>', so without it the context 'ctx' would
    // also match every session of a user whose context is 'ctx2'.
    const prefix = ColimindService.COLIMIND_STORAGE_PREFIX + userIdentifier + '_';
    // get all sessions for the user from storage
    const keys: string[] = [];
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i);
      if (key?.startsWith(prefix)) {
        keys.push(key);
      }
    }
    for (const key of keys) {
      const session = this.getFromStorage(key);
      if (session) {
        sessions.push(session);
      }
    }
    // updated_at is an ISO string, so a lexicographic compare sorts chronologically
    return sessions.sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? ''));
  }

  /** ISO 8601 so stored sessions stay sortable and parsable; formatting happens in the view. */
  private eventTimestampToIso(timestamp: number | string): string {
    const ts = Number(timestamp);
    const date = new Date(ts < 1000000000000 ? ts * 1000 : ts); // new Date() expects milliseconds since Unix epoch, but event timestamp is a Unix timestamp in seconds ==> convert here
    return isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString();
  }
}
